<script setup lang="ts">
import { Camera, Sparkles } from '@lucide/vue';
import packageJson from '../package.json';
import Typography from './design-system/components/Typography.vue';
import ComponentShowcase from './design-system/components/ComponentShowcase.vue';

const version = `v${packageJson.version}`;

// --- HTML SNIPPETS (Storytelling Themed) ---

const btnCode = `<button class="babel-button babel-button--primary">Publish Story</button>
<button class="babel-button babel-button--secondary">Save Draft</button>
<button class="babel-button babel-button--ghost">Preview</button>
<button class="babel-button babel-button--secondary" disabled>Rendering...</button>`;

const inputCode = `<div class="babel-input-wrapper">
  <label class="babel-label">Project Title</label>
  <input type="text" class="babel-input" placeholder="e.g. Q3 Brand Documentary" />
  <p class="babel-helper-text">This will be visible to all collaborators.</p>
</div>`;

const checkboxCode = `<label class="babel-checkbox-wrapper">
  <input type="checkbox" class="babel-checkbox" />
  <span class="babel-text-body" style="font-size: 0.875rem;">Enable 4K Export</span>
</label>`;

const radioCode = `<div class="babel-stack" style="gap: 8px;">
  <label class="babel-radio-wrapper">
    <input type="radio" name="aspect" class="babel-radio" checked />
    <span class="babel-text-body" style="font-size: 0.875rem;">16:9 (Cinematic)</span>
  </label>
  <label class="babel-radio-wrapper">
    <input type="radio" name="aspect" class="babel-radio" />
    <span class="babel-text-body" style="font-size: 0.875rem;">9:16 (Social Vertical)</span>
  </label>
</div>`;

const toggleCode = `<label class="babel-checkbox-wrapper">
  <input type="checkbox" class="babel-toggle" checked />
  <span class="babel-text-body" style="font-size: 0.875rem;">Auto-Generate Subtitles</span>
</label>`;

const selectCode = `<div class="babel-input-wrapper">
  <label class="babel-label">Color Grade</label>
  <select class="babel-select">
    <option>Select a LUT...</option>
    <option>Documentary Contrast</option>
    <option>Neon Noir</option>
    <option>Golden Hour Soft</option>
  </select>
</div>`;

const rangeCode = `<div class="babel-input-wrapper">
  <label class="babel-label">Audio Ducking Intensity</label>
  <input type="range" class="babel-range" min="0" max="100" value="75" />
</div>`;

const dividerCode = `<hr class="babel-divider" />
<div class="babel-divider--text">Director's Notes</div>`;

const accordionCode = `<details class="babel-accordion">
  <summary>Export Settings</summary>
  <div class="babel-accordion-content">
    Render format is set to ProRes 422 HQ. Audio will be mastered to -14 LUFS to meet broadcast standards.
  </div>
</details>`;

const tabsCode = `<div class="babel-tabs">
  <a href="#" class="babel-tab babel-tab--active">Timeline</a>
  <a href="#" class="babel-tab">Media Pool</a>
  <a href="#" class="babel-tab">Color Grading</a>
</div>`;

const breadcrumbCode = `<div class="babel-breadcrumbs">
  <a href="#" class="babel-breadcrumb">Workspace</a>
  <span class="babel-breadcrumb-separator">/</span>
  <a href="#" class="babel-breadcrumb">Q3 Brand Campaign</a>
  <span class="babel-breadcrumb-separator">/</span>
  <span class="babel-breadcrumb" style="color: var(--babel-color-white);">Sequence 01</span>
</div>`;

const paginationCode = `<div class="babel-pagination">
  <a href="#" class="babel-page-btn">&larr;</a>
  <a href="#" class="babel-page-btn babel-page-btn--active">1</a>
  <a href="#" class="babel-page-btn">2</a>
  <a href="#" class="babel-page-btn">3</a>
  <a href="#" class="babel-page-btn">&rarr;</a>
</div>`;

const modalCode = `<!-- <div class="babel-modal-backdrop"> -->
  <div class="babel-modal">
    <h3 class="babel-text-h3">Delete Sequence?</h3>
    <p class="babel-text-body" style="margin-bottom: 24px;">Are you sure you want to delete "Sequence 01"? All linked assets will be removed from the timeline.</p>
    <div class="babel-cluster">
      <button class="babel-button babel-button--secondary">Cancel</button>
      <button class="babel-button babel-button--primary" style="background: #ef4444; box-shadow: none; color: white;">Delete Sequence</button>
    </div>
  </div>
<!-- </div> -->`;

const toastCode = `<div class="babel-toast" style="position: static;">
  <span style="color: var(--babel-color-gold); font-weight: bold;">✓</span>
  <span>Story changes saved.</span>
</div>`;

const spinnerCode = `<div class="babel-cluster" style="flex-direction: column; gap: 12px;">
  <div class="babel-spinner"></div>
  <span class="babel-text-caption">Rendering Timeline...</span>
</div>`;

const tableCode = `<div class="babel-table-container">
  <table class="babel-table">
    <thead>
      <tr>
        <th>Asset Name</th>
        <th>Format</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>A-Roll_Interview_01.mov</td>
        <td>ProRes 422</td>
        <td><span class="babel-badge babel-badge--success">Online</span></td>
      </tr>
      <tr>
        <td>B-Roll_Drone_City.mp4</td>
        <td>H.264</td>
        <td><span class="babel-badge babel-badge--gold">Proxy</span></td>
      </tr>
      <tr>
        <td>Audio_Master.wav</td>
        <td>WAV 48kHz</td>
        <td><span class="babel-badge babel-badge--neutral">Offline</span></td>
      </tr>
    </tbody>
  </table>
</div>`;

const emptyStateCode = `<div class="babel-empty-state">
  <div class="babel-avatar" style="width: 64px; height: 64px; margin-bottom: 16px; background: rgba(255,255,255,0.05); color: var(--babel-color-white); border-color: rgba(255,255,255,0.1);">
    <span style="font-size: 24px;">🎬</span>
  </div>
  <h3 class="babel-text-h3">The canvas is blank</h3>
  <p class="babel-text-body" style="margin-bottom: 24px; max-width: 300px;">Every great story starts with a blank canvas. Import your first asset to begin.</p>
  <button class="babel-button babel-button--primary">Import Media</button>
</div>`;

</script>

<template>
  <div class="app-container babel-scope">
    <header class="app-header">
      <div class="header-inner">
        <a href="#top" class="brand-link">
          <div class="brand-icon">
            <Camera :size="20" class="brand-camera" />
          </div>
          <div class="brand-text">
            <strong>Babel</strong>
            <small>Narrative System</small>
          </div>
        </a>
        <nav class="app-nav">
          <a href="#triggers">Triggers</a>
          <a href="#capture">Capture</a>
          <a href="#structure">Structure</a>
          <a href="#feedback">Feedback</a>
        </nav>
      </div>
    </header>

    <main id="top" class="app-main">
      <section class="hero-section">
        <div class="hero-bg"></div>
        <Typography variant="caption" class="hero-eyebrow">
          <Sparkles :size="16" /> Babel Narrative System {{ version }}
        </Typography>
        <Typography variant="h1" class="hero-title">
          Assemble Your <span class="babel-text-gold italic">Narrative.</span>
        </Typography>
        <Typography variant="body" class="hero-subtitle">
          Effortlessly craft premium video storytelling experiences with modular, zero-dependency components. Beautiful by default.
        </Typography>
      </section>

      <div class="library-container">
        <!-- Sidebar Navigation -->
        <aside class="library-sidebar">
          <div class="sidebar-sticky">
            <Typography variant="h3" as="h4">Interaction Triggers</Typography>
            <ul class="sidebar-nav">
              <li><a href="#buttons">Buttons</a></li>
              <li><a href="#links">Links</a></li>
            </ul>

            <Typography variant="h3" as="h4" style="margin-top: 24px;">Data Capture</Typography>
            <ul class="sidebar-nav">
              <li><a href="#inputs">Text Input</a></li>
              <li><a href="#checkboxes">Checkboxes</a></li>
              <li><a href="#radios">Radios</a></li>
              <li><a href="#toggles">Toggles</a></li>
              <li><a href="#select">Select Dropdowns</a></li>
              <li><a href="#range">Range Sliders</a></li>
            </ul>

            <Typography variant="h3" as="h4" style="margin-top: 24px;">Narrative Structure</Typography>
            <ul class="sidebar-nav">
              <li><a href="#dividers">Dividers</a></li>
              <li><a href="#accordions">Accordions</a></li>
              <li><a href="#tabs">Tabs</a></li>
              <li><a href="#breadcrumbs">Breadcrumbs</a></li>
            </ul>

            <Typography variant="h3" as="h4" style="margin-top: 24px;">Feedback & Display</Typography>
            <ul class="sidebar-nav">
              <li><a href="#modals">Modals</a></li>
              <li><a href="#toasts">Toasts</a></li>
              <li><a href="#spinners">Spinners</a></li>
              <li><a href="#tables">Media Tables</a></li>
              <li><a href="#empty">Empty States</a></li>
            </ul>
          </div>
        </aside>

        <!-- Components Content -->
        <div class="library-content">
          
          <!-- TRIGGERS -->
          <div id="triggers" class="section-divider">
            <Typography variant="h2">Interaction Triggers</Typography>
          </div>

          <section id="buttons" class="component-section">
            <ComponentShowcase title="Buttons" :htmlCode="btnCode" description="Cinematic actions with disabled state support.">
              <div v-html="btnCode" class="babel-cluster"></div>
            </ComponentShowcase>
          </section>

          <!-- DATA CAPTURE -->
          <div id="capture" class="section-divider">
            <Typography variant="h2">Data Capture</Typography>
          </div>

          <section id="inputs" class="component-section">
            <ComponentShowcase title="Text Input" :htmlCode="inputCode" description="Premium input fields featuring helper text context.">
              <div v-html="inputCode" style="width: 100%; max-width: 320px;"></div>
            </ComponentShowcase>
          </section>

          <section id="checkboxes" class="component-section">
            <ComponentShowcase title="Checkbox" :htmlCode="checkboxCode">
              <div v-html="checkboxCode"></div>
            </ComponentShowcase>
          </section>

          <section id="radios" class="component-section">
            <ComponentShowcase title="Radio Buttons" :htmlCode="radioCode">
              <div v-html="radioCode"></div>
            </ComponentShowcase>
          </section>

          <section id="toggles" class="component-section">
            <ComponentShowcase title="Toggle Switch" :htmlCode="toggleCode">
              <div v-html="toggleCode"></div>
            </ComponentShowcase>
          </section>

          <section id="select" class="component-section">
            <ComponentShowcase title="Select Dropdown" :htmlCode="selectCode">
              <div v-html="selectCode" style="width: 100%; max-width: 320px;"></div>
            </ComponentShowcase>
          </section>

          <section id="range" class="component-section">
            <ComponentShowcase title="Range Slider" :htmlCode="rangeCode">
              <div v-html="rangeCode" style="width: 100%; max-width: 320px;"></div>
            </ComponentShowcase>
          </section>

          <!-- STRUCTURE -->
          <div id="structure" class="section-divider">
            <Typography variant="h2">Narrative Structure</Typography>
          </div>

          <section id="dividers" class="component-section">
            <ComponentShowcase title="Dividers" :htmlCode="dividerCode">
              <div v-html="dividerCode" style="width: 100%;"></div>
            </ComponentShowcase>
          </section>

          <section id="accordions" class="component-section">
            <ComponentShowcase title="Accordion (Native)" :htmlCode="accordionCode">
              <div v-html="accordionCode" style="width: 100%; max-width: 400px;"></div>
            </ComponentShowcase>
          </section>

          <section id="tabs" class="component-section">
            <ComponentShowcase title="Tabs" :htmlCode="tabsCode">
              <div v-html="tabsCode" style="width: 100%;"></div>
            </ComponentShowcase>
          </section>

          <section id="breadcrumbs" class="component-section">
            <ComponentShowcase title="Breadcrumbs" :htmlCode="breadcrumbCode">
              <div v-html="breadcrumbCode" style="width: 100%;"></div>
            </ComponentShowcase>
          </section>

          <!-- FEEDBACK -->
          <div id="feedback" class="section-divider">
            <Typography variant="h2">Feedback & Display</Typography>
          </div>

          <section id="modals" class="component-section">
            <ComponentShowcase title="Modal / Dialog" :htmlCode="modalCode" description="Focused overlays for critical user decisions.">
              <div v-html="modalCode" style="width: 100%; display: flex; justify-content: center;"></div>
            </ComponentShowcase>
          </section>

          <section id="toasts" class="component-section">
            <ComponentShowcase title="Toast Notification" :htmlCode="toastCode">
              <div v-html="toastCode"></div>
            </ComponentShowcase>
          </section>

          <section id="spinners" class="component-section">
            <ComponentShowcase title="Loading Spinner" :htmlCode="spinnerCode">
              <div v-html="spinnerCode"></div>
            </ComponentShowcase>
          </section>

          <section id="tables" class="component-section">
            <ComponentShowcase title="Data Table" :htmlCode="tableCode" description="Clear, compact metadata display for media assets.">
              <div v-html="tableCode" style="width: 100%;"></div>
            </ComponentShowcase>
          </section>

          <section id="empty" class="component-section">
            <ComponentShowcase title="Empty State" :htmlCode="emptyStateCode" description="Inspiring blank canvas screens for onboarding.">
              <div v-html="emptyStateCode" style="width: 100%;"></div>
            </ComponentShowcase>
          </section>

        </div>
      </div>
    </main>

    <footer class="app-footer">
      <Typography variant="body">&copy; {{ new Date().getFullYear() }} Babel Narrative System. Built for scale.</Typography>
    </footer>
  </div>
</template>

<style lang="scss">
body, html {
  margin: 0;
  padding: 0;
  background-color: var(--babel-color-base);
  color: var(--babel-color-ink);
  scroll-behavior: smooth;
}

.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: rgba(15, 15, 16, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--babel-color-border);
}

.header-inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--babel-space-5);
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand-link {
  display: flex;
  align-items: center;
  gap: var(--babel-space-3);
  text-decoration: none;
  color: var(--babel-color-white);
  transition: opacity 0.3s;
}

.brand-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(212, 166, 61, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-camera {
  color: var(--babel-color-gold);
}

.brand-text {
  display: flex;
  flex-direction: column;
}

.brand-text strong {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.brand-text small {
  font-size: 0.75rem;
  color: var(--babel-color-gold);
}

.app-nav {
  display: none;
  gap: var(--babel-space-5);
}

@media (min-width: 768px) {
  .app-nav {
    display: flex;
  }
}

.app-nav a {
  text-decoration: none;
  color: var(--babel-color-muted);
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.3s;
}

.app-nav a:hover {
  color: var(--babel-color-white);
}

.app-main {
  flex: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 120px var(--babel-space-5) var(--babel-space-10);
  width: 100%;
}

.hero-section {
  position: relative;
  margin-bottom: var(--babel-space-10);
}

.hero-bg {
  position: absolute;
  top: -100px;
  right: 0;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(212, 166, 61, 0.08) 0%, rgba(15, 15, 16, 0) 70%);
  z-index: -1;
  pointer-events: none;
}

.hero-eyebrow {
  display: flex;
  align-items: center;
  gap: var(--babel-space-2);
  color: var(--babel-color-gold);
  margin-bottom: var(--babel-space-4);
}

.hero-title {
  max-width: 800px;
}

.hero-title .italic {
  font-style: italic;
}

.hero-subtitle {
  max-width: 600px;
  font-size: 1.25rem;
  line-height: 1.6;
}

.library-container {
  display: grid;
  gap: var(--babel-space-6);
}

@media (min-width: 1024px) {
  .library-container {
    grid-template-columns: 240px 1fr;
    gap: var(--babel-space-10);
  }
}

.sidebar-sticky {
  position: sticky;
  top: 120px;
}

.sidebar-nav {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: var(--babel-space-2);
}

.sidebar-nav a {
  text-decoration: none;
  color: var(--babel-color-muted);
  font-size: 0.95rem;
  transition: color 0.2s;
  display: block;
  padding: 4px 0;
}

.sidebar-nav a:hover {
  color: var(--babel-color-gold);
}

.section-divider {
  margin-top: var(--babel-space-10);
  margin-bottom: var(--babel-space-6);
  padding-bottom: var(--babel-space-3);
  border-bottom: 1px solid var(--babel-color-border);
}

.component-section {
  scroll-margin-top: 120px;
}

.app-footer {
  text-align: center;
  padding: var(--babel-space-8) 0;
  border-top: 1px solid var(--babel-color-border);
}
</style>
